/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { DemandAnalysis, DemandAnalysisProcessOwner, AddDemandAnalysisDTO, DemandAnalysisStatus, ProcessOwnerDto, Workplace } from '../../api/schemas/schema'
import { useForm } from 'react-hook-form'
import { getDefaultValues, getProcessOwnersByWorkplaceId, getClausesByDemandModuleId } from '../../helpers/demandAssessmentHelper'
import { IComplianceState } from '../../interfaces/IComplianceState'
import { DataContext } from '../../context/DataContext'
import { t } from '../../i18n/i18n'
import { DemandAssessmentForm, DemandAssessmentFormValues } from './DemandAssessmentForm'
import { ModalLoadingDemandAssessment } from './ModalLoadingDemandAssessment'
import { handleMessage } from '../../helpers/stateHelper'
import { api } from '../../helpers/apiHelper'
import { ProcessOwnerForm } from './ProcessOwnerForm'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'

type DemandAssessmentFormProps = {
   setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>
   complianceState: IComplianceState
   workplaces: Workplace[]
}

export const ModalDemandAssessment = ({ setComplianceState, complianceState, workplaces }: DemandAssessmentFormProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount, organisation } = dataContext.state
   const { setRootState } = dataContext
   const { processes, demandComplianceModules } = complianceState
   const demandAssessmentForm = useForm<DemandAssessmentFormValues>({
      defaultValues: {
         ...getDefaultValues(dataContext, complianceState),
         demandComplianceModuleIds: null,
      },
   })

   const {
      reset,
      trigger,
      handleSubmit,
      watch,
      formState: { isValid, isSubmitSuccessful, isSubmitted },
   } = demandAssessmentForm

   const { leadershipPerspectives, open, subjects } = complianceState
   const [errorState, setErrorState] = useState<{ message: string; isNetwork: boolean } | null>(null)
   const [activeStep, setActiveStep] = useState<number>(1)
   const [defaultProcessOwners, setDefaultProcessOwners] = useState<ProcessOwnerDto[]>([])
   const [processOwnerLoading, setProcessOwnerLoading] = useState<boolean>(false)

   const nextStep = async () => {
      await trigger()
      if (isValid) {
         const selectedWorkplace = watch('workplace')

         const defaultProcessOwners = await getProcessOwnersByWorkplaceId(selectedWorkplace.id, dataContext, setProcessOwnerLoading)

         setDefaultProcessOwners(defaultProcessOwners ?? [])
         setActiveStep(activeStep + 1)
      }
   }

   const prevStep = () => setActiveStep(activeStep - 1)
   const onSubmit = async (data: DemandAssessmentFormValues) => {
      await trigger()
      if (isValid) {
         try {
            data.organisationId = organisation.id
            data.createdBy = userAccount
            data.modifiedBy = userAccount
            data.createdById = userAccount.id
            data.modifiedById = userAccount.id
            data.subjects = subjects.map((s) => s.text)
            data.leadershipPerspectives = leadershipPerspectives.map((lp) => lp.text)
            data.workplaceId = data.workplace?.id

            if (organisation.isSandbox) {
               data.processes = processes.map((p) => p.text)
               const goodPracticeModule = demandComplianceModules.find((m) => m.systemCode === 'GOOD_PRACTICE')
               if (goodPracticeModule) {
                  data.demandComplianceModuleIds = goodPracticeModule.id
                  const clauses = await getClausesByDemandModuleId(goodPracticeModule.id, dataContext, () => {})
                  data.selectedClauses = clauses
               }
            }

            if (data.processes.length > 0 && !organisation.isSandbox) {
               data.demandAnalysisProcessOwners = data.demandAnalysisProcessOwners.map((po) => ({ ...po, process: null }) as DemandAnalysisProcessOwner)
            } else {
               data.demandAnalysisProcessOwners = []
            }

            const dto: AddDemandAnalysisDTO = {
               demandAnalysis: data,
               selectedClauses: data.selectedClauses,
               demandComplianceModuleIds: data.demandComplianceModuleIds ? [data.demandComplianceModuleIds] : [],
               organisationTypeId: organisation.organisationTypeId,
               organisationSizeId: organisation.organisationSizeId,
               onlyDemandModule: data.onlyDemandModule,
            }

            setComplianceState((prev) => ({ ...prev, open: false }))
            const demandAnalysis: DemandAnalysis = (await api().addDemandAnalysis(dto)).data
            reset(demandAnalysis, { keepIsSubmitSuccessful: true, keepIsSubmitted: true })
         } catch (error: any) {
            const networkError = error?.status === undefined
            let errorMessage: string
            if (networkError) {
               errorMessage = t('AssessmentMayHaveBeenCreated')
               handleMessage(setRootState, 'warning', errorMessage, '')
            } else {
               errorMessage = error?.error?.message === 'No matching questions found.' ? t('NoMatchingQuestonsFound') : t('SomethingWentWrong')
               handleMessage(setRootState, 'error', errorMessage, error?.error?.message ?? '')
            }
            setErrorState({ message: errorMessage, isNetwork: networkError })
            setActiveStep(1)
         }
      }
   }
   useEffect(() => {
      if (complianceState.demandAnalysisStatuses.length > 0) {
         reset(
            {
               status: DemandAnalysisStatus.Ongoing,
            },
            { keepDefaultValues: true, keepValues: true }
         )
      }
   }, [complianceState.demandAnalysisStatuses.length])

   useEffect(() => {
      if (isSubmitted) {
         if (!isSubmitSuccessful) {
            setComplianceState((prev) => ({ ...prev, open: true }))
         }
      }
   }, [isSubmitted])

   return (
      <>
         <ModalLoadingDemandAssessment
            demandAssessmentForm={demandAssessmentForm}
            setComplianceState={setComplianceState}
            complianceState={complianceState}
            error={errorState?.message}
            isNetworkError={errorState?.isNetwork}
         />

         <DraggableModal
            title={activeStep === 1 ? t('NewDemandAssessment') : t('ProcessOwnersForDemandAssessment')}
            open={open}
            onClose={() => {
               setComplianceState((prev) => ({ ...prev, open: false }))
               reset(getDefaultValues(dataContext, complianceState))
            }}
         >
            <Form onSubmit={handleSubmit(onSubmit)}>
               {activeStep === 1 && (
                  <DemandAssessmentForm
                     demandAssessmentForm={demandAssessmentForm}
                     setComplianceState={setComplianceState}
                     complianceState={complianceState}
                     nextStep={nextStep}
                     workplaces={workplaces}
                  />
               )}
               {activeStep === 2 && !processOwnerLoading && (
                  <ProcessOwnerForm
                     demandAssessmentForm={demandAssessmentForm}
                     prevStep={prevStep}
                     processes={complianceState.processes}
                     defaultProcessOwners={defaultProcessOwners}
                  />
               )}
            </Form>
         </DraggableModal>
      </>
   )
}
